// src/contexts/QuranSettingsContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import quranEditions from '../models/quranEditions';

// Font options available for Quran text
export const AVAILABLE_FONTS = [
  { name: "Al-Kitab", family: "'Al-Kitab', 'Amiri', serif" },
  { name: "Uthmani", family: "'Uthmani', 'Scheherazade New', serif" },
  { name: "Naskh", family: "'Naskh', 'Scheherazade New', serif" },
  { name: "Al-Qalam", family: "'Al-Qalam', 'Traditional Arabic', serif" },
  { name: "IndoPak", family: "'IndoPak', 'Jameel Noori Nastaleeq', serif" },
  { name: "KFGQPC", family: "'KFGQPC Uthmanic Script HAFS', serif" },
  { name: "Amiri", family: "'Amiri', serif" },
  { name: "Lateef", family: "'Lateef', serif" },
  { name: "Scheherazade New", family: "'Scheherazade New', serif" },
  { name: "Noto Naskh Arabic", family: "'Noto Naskh Arabic', serif" },
];

// Font weight options
export const FONT_WEIGHTS = [
  { name: "Normal", value: "normal" },
  { name: "Bold", value: "bold" },
  { name: "Semi-Bold", value: "600" },
];

// Font size constants
export const MIN_FONT_SIZE = 16;
export const MAX_FONT_SIZE = 72;
export const FONT_SIZE_STEP = 2;

// Default Quran settings
const DEFAULT_SETTINGS = {
  fontFamily: "'Scheherazade New', 'Amiri', serif",
  fontSize: 36,
  fontWeight: "bold",
  showTajweed: true,
  wordByWord: false,
  showTranslation: true,
  showTransliteration: false,
  translationId: 'en.sahih',
  showTafsir: false,
  tafsirId: 'en.tafsir-ibn-kathir',
  reciterId: 'ar.mahermuaiqly',
  autoPlayAyahs: true,
  navigationMode: 'page', // 'surah', 'page', 'juz', 'hizb', 'ruku', 'manzil'
  theme: 'light', // 'light', 'dark', 'sepia'
  showPageView: true,
  quranEdition: quranEditions[0].identifier, // Use the first Quran edition as default
};

// Create the context
const QuranSettingsContext = createContext();

// Settings provider component
export const QuranSettingsProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('quranSettings');
    return savedSettings ? JSON.parse(savedSettings) : DEFAULT_SETTINGS;
  });

  // Update a single setting
  const updateSetting = (key, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: value
    }));
  };

  // Font size adjustment helpers
  const increaseFontSize = () => {
    setSettings(prevSettings => ({
      ...prevSettings,
      fontSize: Math.min(prevSettings.fontSize + FONT_SIZE_STEP, MAX_FONT_SIZE)
    }));
  };

  const decreaseFontSize = () => {
    setSettings(prevSettings => ({
      ...prevSettings,
      fontSize: Math.max(prevSettings.fontSize - FONT_SIZE_STEP, MIN_FONT_SIZE)
    }));
  };

  // Reset settings to defaults
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('quranSettings', JSON.stringify(settings));
  }, [settings]);

  return (
    <QuranSettingsContext.Provider 
      value={{ 
        settings, 
        updateSetting, 
        resetSettings, 
        increaseFontSize, 
        decreaseFontSize,
        quranEditions // Expose editions for use in panels
      }}
    >
      {children}
    </QuranSettingsContext.Provider>
  );
};

// Custom hook to use the settings context
export const useQuranSettings = () => {
  const context = useContext(QuranSettingsContext);
  if (!context) {
    throw new Error('useQuranSettings must be used within a QuranSettingsProvider');
  }
  return context;
};